import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Checkbox, Divider, FormControlLabel, Grid, Link, Switch, Typography } from '@mui/material';
import { theme } from 'theme';
import { jbColors } from 'theme/jbColors';
import { palette } from 'theme/palette';
import { DocumentStatuses, ProposalDocumentClassTypes } from 'types';
import { formatEnum } from 'utils/stringUtils';
import { setSelectedDocuments } from 'reducers/caseReducer';
import { selectSelectedCaseDocuments } from 'selectors/caseSelector';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import CheckIcon from 'assets/icons/Check.svg?react';
import { orderCaseDocuments } from 'services/product-service/orderCaseDocuments';
import { closeModal, openModal } from 'reducers/modalReducer';

const styles = {
  title: {
    color: palette.black,
  },
  divider: {
    backgroundColor: jbColors.app.lightGrey,
  },
  checkbox: {
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
  },
  mandatorySlides: {
    marginLeft: theme.spacing(4),
  },
  pendingIcon: {
    padding: theme.spacing(0.3),
    color: jbColors.technical.amberYellow[100],
  },
  activeIcon: {
    padding: theme.spacing(0.3),
    color: jbColors.technical.forestGreen[80],
  },
  checkIcon: {
    marginLeft: theme.spacing(0.5),
  },
};

type Props = {
  status: DocumentStatuses;
  name: string;
  id: string;
  shortListId?: string;
};

export const ProposalRow = ({ name, status, id, shortListId = '' }: Props) => {
  const dispatch = useDispatch();
  const selectedDocuments = useSelector(selectSelectedCaseDocuments);
  const statusText = status === DocumentStatuses.READY_TO_ORDER ? '-' : formatEnum(status);
  // const checkBoxDisabled = ( status === DocumentStatuses.READY_TO_ORDER || status === DocumentStatuses.ORDERED ) && name === ProposalDocumentClassTypes.CLIENT_PRESENTATION;
  const checkBoxDisabled =
    ((status === DocumentStatuses.READY_TO_ORDER || status === DocumentStatuses.ORDERED) &&
      name === ProposalDocumentClassTypes.CLIENT_PRESENTATION) ||
    (status !== DocumentStatuses.FAILED && status !== DocumentStatuses.READY_TO_ORDER);
  const [documentStatus, setDocumentStatus] = useState(status);

  const orderClientPresentation = async () => {
    try {
      await orderCaseDocuments([id]);
      setDocumentStatus(DocumentStatuses.ORDERED);
    } catch (error: unknown) {
      dispatch(
        openModal({
          modalType: 'JBErrorModalWrapper',
          modalProps: {
            httpError: error,
            okHandler: () => {
              dispatch(closeModal());
            },
            technicalInfo: {
              title: 'Error when ordering documents.',
              userErrorMessage: 'An error occurred ordering documents. Please try again later.',
            },
          },
        }),
      );
      return 'error';
    }
  };

  const handleSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      dispatch(setSelectedDocuments([...selectedDocuments, id]));
    } else {
      const docs = selectedDocuments.filter((docId) => docId !== id).filter((x) => x !== shortListId);
      dispatch(setSelectedDocuments(docs));
    }
  };

  const handleOnlyMandatorySelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      dispatch(setSelectedDocuments([...selectedDocuments.filter((docId) => docId !== id), shortListId]));
    } else {
      dispatch(setSelectedDocuments([...selectedDocuments.filter((docId) => docId !== shortListId), id]));
    }
  };

  return (
    <Grid data-testid="proposal-row">
      <Divider sx={styles.divider} />
      <Grid container justifyContent="space-between" alignItems={'center'}>
        <Grid item xs={3}>
          <FormControlLabel
            control={
              <Checkbox
                sx={styles.checkbox}
                id={`document-selection-checkbox-${id}`}
                checked={selectedDocuments.includes(id) || selectedDocuments.includes(shortListId)}
                onChange={handleSelection}
                color="primary"
                disabled={checkBoxDisabled}
                inputProps={{
                  // @ts-expect-error data-testid is a valid prop
                  'data-testid': `document-selection-checkbox-${id}`,
                }}
              />
            }
            label={formatEnum(name)}
          />
        </Grid>
        <Grid item xs={4}>
          <Grid container>
            {name === ProposalDocumentClassTypes.CLIENT_PRESENTATION &&
            documentStatus === DocumentStatuses.READY_TO_ORDER ? (
              <Link
                onClick={orderClientPresentation}
                underline={'always'}
                style={{
                  cursor: 'pointer',
                  color: jbColors.support.greenSmoke[80],
                }}
                color={'primary'}
              >
                Order presentation
              </Link>
            ) : name === ProposalDocumentClassTypes.CLIENT_PRESENTATION &&
              documentStatus === DocumentStatuses.ORDERED ? (
              <Typography style={{ fontStyle: 'italic' }} variant={'caption'}>
                {' '}
                Preparing...{' '}
              </Typography>
            ) : (
              <Typography>{statusText}</Typography>
            )}
            {status === DocumentStatuses.PENDING && <FiberManualRecordIcon sx={styles.pendingIcon} />}
            {status === DocumentStatuses.ACTIVE && <FiberManualRecordIcon sx={styles.activeIcon} />}
            {status === DocumentStatuses.SENT && (
              <Grid>
                <CheckIcon style={styles.checkIcon} />
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
      {name === ProposalDocumentClassTypes.CLIENT_PRESENTATION && (
        <Grid sx={styles.mandatorySlides}>
          <FormControlLabel
            control={
              <Switch
                color="primary"
                id={`only-mandatory-slides-switch-${id}`}
                name={`only-mandatory-slides-switch`}
                checked={selectedDocuments.includes(shortListId)}
                disabled={!selectedDocuments.includes(id) && !selectedDocuments.includes(shortListId)}
                onChange={handleOnlyMandatorySelection}
                inputProps={{
                  // @ts-expect-error data-testid is a valid prop
                  'data-testid': `only-mandatory-slides-switch-${id}`,
                }}
              />
            }
            label={'Only mandatory slides'}
          />
        </Grid>
      )}
    </Grid>
  );
};
